import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/clinic/Navbar";
import Footer from "@/components/clinic/Footer";
import { applyJsonLd, applySeo, SITE_URL } from "@/lib/seo";
import { telHref, whatsappHref, clinicNAP } from "@/lib/clinic";

const PATH = "/blepharoplasty-recovery-guide";

const timeline = [
  {
    stage: "Day 0",
    label: "Surgery day",
    detail:
      "Upper blepharoplasty takes 45–60 minutes under local anaesthesia (90–120 minutes for combined upper and lower). You walk out the same day with fine tapes over the incisions. Expect tightness rather than pain, mild blurring from ointment, and someone to take you home.",
    care: ["Cool compresses, 10 minutes on / 10 off", "Head elevated on two pillows", "No driving, no alcohol"],
  },
  {
    stage: "Days 1–2",
    label: "Peak swelling and bruising",
    detail:
      "Swelling and bruising typically peak in the first 48 hours. Lids may feel heavy and look worse before they look better — this is the expected curve, not a complication.",
    care: ["Compresses through the day", "Sleep propped up", "Paracetamol is usually enough", "Avoid bending and lifting"],
  },
  {
    stage: "Days 3–5",
    label: "The turn",
    detail:
      "Bruising begins to shift from purple to yellow-green and settles downward toward the cheek. Most desk-based patients working from home resume light screen work here, in short sessions with plenty of blinking breaks.",
    care: ["Light walking is encouraged", "Lubricating drops for dryness", "Sunglasses outdoors"],
  },
  {
    stage: "Day 7",
    label: "Stitch removal",
    detail:
      "Stitches are removed at your day-7 review in our central-London suite. The incision sits in the natural upper-lid crease and is barely perceptible once healed. Dr Vahe checks lid closure, symmetry and scar line at this visit.",
    care: ["Attend the day-7 appointment", "Gentle cleansing around the lids", "Still no gym"],
  },
  {
    stage: "Days 7–10",
    label: "Social downtime ends",
    detail:
      "Most patients are comfortable returning to social engagements and the office within 7–10 days. Residual puffiness is usually camouflageable with light concealer once the incisions are sealed.",
    care: ["Makeup once cleared at your review", "Back to full screen and office days", "Camera-ready for most patients"],
  },
  {
    stage: "Weeks 2–3",
    label: "Swelling resolves",
    detail:
      "Subtle residual swelling fully resolves by week three, and the crease begins to settle into its final position. Exercise is typically reintroduced gradually from around two weeks, guided by your review.",
    care: ["Reintroduce cardio gradually", "Daily SPF on the scar line", "Avoid swimming pools until cleared"],
  },
  {
    stage: "Months 2–6",
    label: "Final refinement",
    detail:
      "Scars continue to fade and soften over several months. The result matures quietly — the aim is rested eyes that look like you, not like surgery.",
    care: ["Continued SPF", "Scar care if advised", "Follow-up review as scheduled"],
  },
];

const faqs = [
  {
    q: "How long does upper blepharoplasty recovery take?",
    a: "Stitches come out on day 7 and most patients return to social and professional life within 7–10 days. Subtle residual swelling fully resolves by around week three, with scars continuing to fade over several months.",
  },
  {
    q: "When are blepharoplasty stitches removed?",
    a: "Stitches are removed at your day-7 review at our central-London suite. Removal takes a few minutes and is not painful.",
  },
  {
    q: "How much social downtime should I plan for?",
    a: "Plan 7–10 days of social downtime. Many desk-based patients work from home from around day 3 and return to the office once stitches are out.",
  },
  {
    q: "How do I manage swelling and bruising after eyelid surgery?",
    a: "Use cool compresses in 10-minute cycles for the first 48 hours, keep your head elevated while sleeping, avoid bending, lifting, alcohol and blood-thinning supplements, and walk gently to keep circulation moving.",
  },
  {
    q: "When can I wear makeup or exercise again?",
    a: "Makeup is usually permitted once the incisions are sealed and cleared at your day-7 review. Cardio is typically reintroduced from around two weeks, with strenuous training later, guided by your surgeon.",
  },
  {
    q: "Is the recovery different for lower blepharoplasty?",
    a: "Lower-lid and combined upper and lower surgery generally involves a little more swelling and bruising, so allow toward the longer end of the 7–10 day window. The combined procedure takes 90–120 minutes.",
  },
];

const RecoveryGuide = () => {
  useEffect(() => {
    applySeo({
      title: "Blepharoplasty Recovery Timeline: Day by Day | London",
      description:
        "Upper blepharoplasty recovery day by day: peak swelling, day-7 stitch removal, 7–10 days social downtime and full settling by week three, from Dr Vahe Clinic in London.",
      path: PATH,
      ogType: "article",
    });

    const cleanup = applyJsonLd("recovery-guide", [
      {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        "@id": `${SITE_URL}${PATH}#webpage`,
        url: `${SITE_URL}${PATH}`,
        name: "Blepharoplasty Recovery Timeline: A Day-by-Day Guide",
        description:
          "A day-by-day upper and lower blepharoplasty recovery timeline covering swelling, bruising, day-7 stitch removal and 7–10 days of social downtime.",
        inLanguage: "en-GB",
        specialty: { "@type": "MedicalSpecialty", name: "PlasticSurgery" },
        about: {
          "@type": "MedicalProcedure",
          name: "Blepharoplasty",
          procedureType: "https://schema.org/SurgicalProcedure",
          bodyLocation: "Eyelid",
          followup:
            "Stitches removed on day 7; social downtime 7–10 days; residual swelling resolves by week three.",
        },
        publisher: { "@id": `${SITE_URL}/#organization` },
        isPartOf: { "@id": `${SITE_URL}/#website` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: "Blepharoplasty Recovery Guide",
            item: `${SITE_URL}${PATH}`,
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);

    return () => cleanup();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
          >
            ← Back to clinic
          </Link>

          <header className="mt-8 mb-12">
            <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
              Patient Guide
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-4 text-foreground leading-tight">
              Blepharoplasty Recovery{" "}
              <span className="italic">Timeline</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground font-light leading-relaxed">
              What upper and lower eyelid surgery actually feels like, hour by hour and week by
              week — written for the patient who needs to know exactly when they can be back in a
              meeting.
            </p>
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-muted-foreground">
              Reviewed by Dr Vahe · Central London
            </p>
          </header>

          <section className="mb-14 border border-border bg-linen p-6 md:p-8">
            <h2 className="font-display text-2xl text-foreground">The short answer</h2>
            <ul className="mt-4 space-y-2 text-foreground/85 font-light">
              <li>Swelling and bruising peak in the first 48 hours.</li>
              <li>Stitches are removed on day 7.</li>
              <li>Social downtime is 7–10 days for most patients.</li>
              <li>Residual swelling fully resolves by week three.</li>
              <li>Scars keep softening over the following months.</li>
            </ul>
          </section>

          <section className="mb-14">
            <h2 className="font-display text-3xl text-foreground mb-8">
              Day by <span className="italic">day</span>
            </h2>
            <ol className="space-y-10 border-l border-border pl-6 md:pl-8">
              {timeline.map((t) => (
                <li key={t.stage} className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[31px] md:-left-[39px] top-2 h-2 w-2 rounded-full bg-accent"
                  />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-accent font-medium">
                    {t.stage}
                  </span>
                  <h3 className="font-display text-xl md:text-2xl mt-2 text-foreground">
                    {t.label}
                  </h3>
                  <p className="mt-3 text-foreground/85 font-light leading-relaxed">{t.detail}</p>
                  <ul className="mt-4 space-y-1 text-sm text-muted-foreground font-light">
                    {t.care.map((c) => (
                      <li key={c}>· {c}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </section>

          <section className="mb-14">
            <h2 className="font-display text-3xl text-foreground mb-6">
              Planning your <span className="italic">downtime</span>
            </h2>
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">
                  Typical return-to-activity windows after blepharoplasty
                </caption>
                <thead className="bg-linen">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium text-foreground">
                      Activity
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium text-foreground">
                      Typical return
                    </th>
                  </tr>
                </thead>
                <tbody className="font-light text-foreground/85">
                  {[
                    ["Light screen work from home", "Day 3–4"],
                    ["Driving", "Once vision is clear and comfortable, often day 3–5"],
                    ["Stitch removal review", "Day 7"],
                    ["Office, social events, makeup", "Day 7–10"],
                    ["Gentle cardio", "From ~2 weeks"],
                    ["Strenuous training, swimming", "From ~3–4 weeks, once cleared"],
                    ["Final settled result", "Weeks 3+ with scar fading over months"],
                  ].map(([a, b]) => (
                    <tr key={a} className="border-t border-border">
                      <td className="px-4 py-3">{a}</td>
                      <td className="px-4 py-3">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-muted-foreground font-light">
              Recovery is individual. These ranges reflect typical experience and are confirmed for
              you at consultation.
            </p>
          </section>

          <section className="mb-14">
            <h2 className="font-display text-3xl text-foreground mb-6">
              When to call <span className="italic">us</span>
            </h2>
            <p className="text-foreground/85 font-light leading-relaxed">
              Contact the clinic promptly if you experience worsening pain rather than easing
              tightness, sudden vision change, one-sided swelling that increases after day two,
              bleeding that does not settle with gentle pressure, or signs of infection such as
              spreading redness, heat or fever. Our coordinator is reachable on{" "}
              <a href={telHref} className="text-accent hover:text-accent/70">
                {clinicNAP.phone.display}
              </a>{" "}
              or{" "}
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent/70"
              >
                WhatsApp
              </a>
              .
            </p>
            <p className="mt-4 text-xs text-muted-foreground font-light">
              This guide is general information, not medical advice, and does not replace a
              consultation. In an emergency, call 999 or attend your nearest A&amp;E.
            </p>
          </section>

          <section className="mb-14">
            <h2 className="font-display text-3xl text-foreground mb-6">
              Recovery <span className="italic">questions</span>
            </h2>
            <dl className="space-y-6">
              {faqs.map((f) => (
                <div key={f.q} className="border-b border-border pb-6">
                  <dt className="font-display text-lg md:text-xl text-foreground">{f.q}</dt>
                  <dd className="mt-3 text-foreground/85 font-light leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="border border-accent/40 bg-linen p-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl text-foreground">
              Plan your recovery around <span className="italic">real dates</span>
            </h2>
            <p className="mt-4 text-muted-foreground font-light">
              See current pricing, surgery dates and before-and-after results, then book a
              consultation with Dr Vahe.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <Link
                to="/#booking"
                className="inline-block bg-primary text-primary-foreground px-8 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-primary/90 transition-colors"
              >
                Book consultation
              </Link>
              <Link
                to="/#results"
                className="inline-block border border-border px-8 py-3 text-[11px] tracking-[0.25em] uppercase text-foreground hover:border-accent transition-colors"
              >
                View results
              </Link>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default RecoveryGuide;
